import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useState } from 'react';

import { postVideoQuestionsActivation } from '@vimeo/gctl-api/fetchers/video/questions/activation';
import { useGctlConfig } from '@vimeo/gctl-api/hooks/GctlProvider';
import { useGetVideoQuestions } from '@vimeo/gctl-api/hooks/useVideoQuestions';
import { useGetVideoQuestionsActivation } from '@vimeo/gctl-api/hooks/useVideoQuestionsActivation';
import type { VideoQuestion } from '@vimeo/gctl-api/models/components/schemas/video-question/VideoQuestion';
import { getPasswordHashFromCookie } from '@vimeo/use-unlocked-video';

import { useGetVideoSignature } from '../hooks/useGetVideoSignature';
import { useContainerDataStore } from '../stores/container-data-store';

interface IViewerQuestionsDataContext {
  questions: VideoQuestion[];
  isActivatingAi: boolean;
  isQuestionsLoading: boolean;
  hasValidStatus: boolean;
  error: Error | null;
  forceRefresh: () => void;
}

const QuestionsDataContext = createContext<IViewerQuestionsDataContext | null>(null);

const STATUS_POLLING_INTERVAL = 5000;

export const QuestionsDataContextProvider = ({
  children,
  videoId,
  guestSignature,
}: {
  children: ReactNode;
  videoId: string;
  guestSignature?: string;
}) => {
  const [shouldPollStatus, setShouldPollStatus] = useState(false);
  const [hasValidStatus, setHasValidStatus] = useState(false);
  const [isActivatingAi, setIsActivatingAi] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const clipId = useContainerDataStore((state) => state.videoId);

  const hash = getPasswordHashFromCookie(videoId);
  const videoSignature = useGetVideoSignature(clipId);
  const { baseUrl, jwt } = useGctlConfig();

  const postActivateVideoQuestions = useCallback(async () => {
    setError(null);
    setIsActivatingAi(true);

    try {
      await postVideoQuestionsActivation({
        // @ts-expect-error videoId expects a number, but doesn't account for clipHash strings
        where: { videoId },
        headers: {
          Authorization: `jwt ${jwt}`,
          'Content-Type': 'application/json',
        },
        baseUrl,
        ...(hash
          ? { query: { password: hash, signature: guestSignature ?? videoSignature } }
          : { query: { signature: guestSignature ?? videoSignature } }),
      });

      setShouldPollStatus(true);
    } catch (err) {
      setIsActivatingAi(false);
      setShouldPollStatus(false);
      setError(err);
    }
  }, [baseUrl, guestSignature, hash, jwt, videoId, videoSignature]);

  const { data: statusData } = useGetVideoQuestionsActivation(
    // @ts-expect-error videoId expects a number, but doesn't account for clipHash strings
    () => {
      if (!videoId) {
        return null;
      }

      return {
        where: { videoId },
        select: ['status'],
        ...(hash ? { query: { password: hash } } : {}),
      };
    },
    {
      refreshInterval: shouldPollStatus ? STATUS_POLLING_INTERVAL : 0,
      revalidateOnFocus: false,
      onSuccess: (data: { status: string }) => {
        if (!hasValidStatus) {
          setHasValidStatus(true);
        }
        if (data?.status === 'PRCS' || data?.status === 'INIT') {
          if (!isActivatingAi) {
            setIsActivatingAi(true);
            setShouldPollStatus(true);
          }
        } else if (data?.status === 'DONE') {
          setIsActivatingAi(false);
          setShouldPollStatus(false);
        } else if (data?.status === 'FAILED' || data?.status === 'INVALID') {
          setIsActivatingAi(false);
          setShouldPollStatus(false);
          setError(new Error('An error occurred during the activation.'));
        } else if (data?.status === 'NONE_EXISTING' && !isActivatingAi) {
          postActivateVideoQuestions();
        }
      },
      onError: (error) => {
        setShouldPollStatus(false);
        setIsActivatingAi(false);
        setError(error);
      },
    },
  );

  const { data: questions, isLoading: isQuestionsLoading } = useGetVideoQuestions(
    // @ts-expect-error videoId expects a number, but doesn't account for clipHash strings
    () => {
      const activationStatus = statusData as unknown as { status?: string };
      if (!videoId || activationStatus?.status !== 'DONE') {
        return null;
      }

      return {
        where: { videoId },
        select: [
          'question',
          'answer',
          'relevantQuotes',
          'relatedQuestions',
          'languageCode',
          'languageLabel',
        ],
        ...(hash ? { query: { password: hash } } : {}),
      };
    },
    {
      revalidateOnFocus: false,
      onError: (err) => setError(err),
    },
  );

  // Backend returns either one of two distinct response types:
  // 1. "status" - Accepted response (HTTP 202): {status: 'PRCS'|'INIT'|'NONE_EXISTING'} - still processing
  // 2. "data" - Collection response (HTTP 200): {data: [...], total, page, ...} - questions ready
  // Although "status" from this endpoint is not being used here like it does in QuestionContext.tsx,
  // instead this file uses the "status" from the activation endpoint to determine if the questions are ready.
  const collectionResponse = questions as unknown as { data?: typeof questions };
  const questionsData = collectionResponse?.data ?? [];

  const value = {
    error,
    videoId,
    hasValidStatus,
    isActivatingAi,
    isQuestionsLoading,
    questions: questionsData,
    forceRefresh: postActivateVideoQuestions,
  };

  return <QuestionsDataContext.Provider value={value}>{children}</QuestionsDataContext.Provider>;
};

export const useQuestionsDataContext = () => {
  const context = useContext(QuestionsDataContext);
  if (!context) {
    throw new Error('useViewerAiContext must be used within a ViewerAiContextProvider');
  }
  return context;
};
